const camelCase = (str: string) => {
	return str.replace(/( |-)./g, (match) => {
		return match.replace(' ', '').replace('-', '').toUpperCase();
	});
};

export const normalizeSourceName = (...parts: string[]) => {
	return parts
		.filter(Boolean)
		.map((part, index) => {
			const trimmedPart = part.trimLeft();
			if (index === 0) {
				return trimmedPart[0].toLowerCase() + camelCase(trimmedPart.slice(1));
			}

			return trimmedPart[0].toUpperCase() + camelCase(trimmedPart.slice(1).toLowerCase());
		})
		.join('');
};

/**
 * Generates a Content ARI for atlassian.design pages in cdstats.
 * Format: ari::ads::page/{pageUrlHash}
 * where pageUrlHash is SHA-1 hash of the URL path (hex encoded)
 *
 * @param urlPath - The URL path (e.g., "/foundations/spacing/primitives")
 * @returns The Content ARI string
 */
export const generateContentARI = (urlPath: string): string => {
	try {
		// Lazy load crypto to avoid SSR issues (only runs on Node/server-side)
		const crypto = require('crypto');
		// Create SHA-1 hash of the URL path
		const hash = crypto.createHash('sha1').update(urlPath).digest('hex');
		return `ari::ads::page/${hash}`;
	} catch (error) {
		// Fallback in case crypto is not available (browser environment or error)
		console.warn('Failed to generate Content ARI:', error);
		return `ari::ads::page/unknown`;
	}
};

/**
 * Generates a Content ARI from the current pathname and extracts the path segment.
 * This is useful for design system documentation pages.
 *
 * @param pathname - The full pathname (e.g., "/components/avatar/usage")
 * @param tabName - Optional tab name to include in ARI generation
 * @returns The Content ARI string
 */
export const generatePageContentARI = (pathname: string, tabName?: string): string => {
	// Extract the meaningful path segments (excluding leading/trailing slashes and trailing filenames)
	const segments = pathname.split('/').filter(Boolean);

	// Build the path we want to hash (e.g., "/components/avatar" or "/foundations/spacing")
	let pathForHashing = '/' + segments.slice(0, Math.max(2, segments.length - 1)).join('/');

	// Optionally append tab name if provided
	if (tabName) {
		pathForHashing += `/${tabName}`;
	}

	return generateContentARI(pathForHashing);
};

/**
 * Calculates the scroll depth of the current page
 * @returns Number between 0 and 1 representing scroll progress
 */
export const calculateScrollDepth = (): number => {
	const windowHeight = window.innerHeight;
	const documentHeight = document.documentElement.scrollHeight;
	const scrollTop = window.scrollY || document.documentElement.scrollTop;

	if (documentHeight === 0) {
		return 0;
	}

	const depth = (scrollTop + windowHeight) / documentHeight;
	return Math.min(Math.max(depth, 0), 1); // Clamp between 0 and 1
};

/**
 * Factory function that returns an analytics client.
 * Purely some sugar to make testing easier.
 */
export const createAnalyticsClient = (options: {
	version: string;
	product: string;
	env: string;
	locale: string;
	useLegacyUrl: boolean;
}): any => {
	// We require this so it's lazily instantiated (doesn't run on SSR!)
	const AnalyticsWebClient = require('@atlassiansox/analytics-web-client').default;
	const { useLegacyUrl, ...opts } = options;
	// See repo for setup instructions:
	// https://bitbucket.org/atlassian/analytics-web-client/src/master/
	return new AnalyticsWebClient(opts, { useLegacyUrl });
};
